import { FC, useMemo } from 'react'
import SecondaryButton from '@pages/Admin/CourseSettings/components/SecondaryButton/SecondaryButton'
import { Select, Spin } from 'antd'
import { LoadingOutlined } from '@ant-design/icons'
import { getCourse } from '../../utils/getCourse'
import EnrollDateCheckbox from '../../../../EnrollDateCheckbox'
import spclasses from '../../../../../SetCourseClassDirectionModal/purchase.module.css'
import classes from './index.module.css'
import classNames from 'classnames'
import PaymentPeriodCheckbox from '../../../PaymentPeriodCheckbox/PaymentPeriodCheckbox'
import CoursePriceView from '@pages/CoursesCatalog/modals/CourseEnrollmentModal/components/CoursePriceView/CoursePriceView'
import { ICourseEnrollmentItem } from './types'

export interface ISelectCourseCard {
  isLoading: boolean
  courses: ICourse[]
  allCourses: ICourse[]
  order: ICourseEnrollmentItem
  promoCode?: IPromoCodeDiscount
  onChange: (
    key: keyof ICourseEnrollmentItem,
    value: number | string | boolean | null | object,
  ) => void
  onDismiss: () => void
}

const SelectCourseCard: FC<ISelectCourseCard> = ({
  isLoading,
  order,
  courses,
  allCourses,
  promoCode,
  onChange,
  onDismiss,
}) => {
  const coursesOptions = useMemo(() => {
    return courses?.map((item) => {
      return {
        value: item.id!.toString(),
        label: item?.name ?? item?.short_name ?? '',
      }
    })
  }, [courses])

  const tariffsOptions = useMemo(() => {
    return (
      getCourse(allCourses, order?.courseId)
        ?.directionTariffs?.filter((tariff) =>
          [
            'free',
            'month',
            'stream_custom_period',
            'stream_custom_days',
            'stream_custom_weeks',
          ].includes(tariff?.type),
        )
        ?.map((item) => {
          return {
            value: item.id!.toString(),
            label: item?.name ?? `Тариф ${item.id}`,
          }
        }) ?? []
    )
  }, [order?.courseId, allCourses])

  const selectedTariff = useMemo(
    () =>
      getCourse(allCourses, order?.courseId)?.directionTariffs.find(
        (item) => item.id === order?.tariffId,
      ),
    [order?.courseId, order?.tariffId, allCourses],
  )

  const onSelectCourse = (value: string | null) => {
    onSelectTariff(null)
    if (!value) {
      onChange('courseId', null)
      return
    }
    onChange('courseId', Number(value) ?? null)
  }

  const onSelectTariff = (value: string | null) => {
    if (!value) {
      onChange('tariffId', null)
      return
    }
    const tariff =
      getCourse(allCourses, order?.courseId)?.directionTariffs?.find(
        (item) => item?.id?.toString() === value,
      ) ?? null
    onChange('tariffId', tariff!.id)
  }

  if (isLoading) {
    return (
      <div className={classes.wrapper}>
        <Spin
          className={classes.loading}
          indicator={
            <LoadingOutlined
              spin
              onPointerEnterCapture={undefined}
              onPointerLeaveCapture={undefined}
            />
          }
        />
      </div>
    )
  }

  const isSelected = order?.courseId !== null && order?.tariffId !== null

  return (
    <div
      className={classNames(
        classes.wrapper,
        isSelected && classes.wrapperSelected,
      )}
    >
      <div className={spclasses.inputWithLabel}>
        <div className={spclasses.inputLabel}>Курс</div>
        <Select
          placeholder="Выберите курс"
          className={spclasses.select}
          value={order?.courseId?.toString()}
          options={coursesOptions}
          onChange={(value) => onSelectCourse(value as string | null)}
          allowClear={false}
        />
      </div>
      {order?.courseId?.toString() && (
        <div className={spclasses.inputWithLabel}>
          <div className={spclasses.inputLabel}>Тариф</div>
          <Select
            placeholder="Выберите тариф"
            className={spclasses.select}
            value={order?.tariffId?.toString()}
            options={tariffsOptions}
            onChange={(value) => onSelectTariff(value as string | null)}
            allowClear={false}
          />
        </div>
      )}
      {selectedTariff?.type &&
        [
          'month',
          'stream_custom_period',
          'stream_custom_days',
          'stream_custom_weeks',
        ].includes(selectedTariff.type) && (
          <EnrollDateCheckbox
            value={order?.enrollDate}
            onChange={(value) => onChange('enrollDate', value)}
          />
        )}
      {selectedTariff && (
        <PaymentPeriodCheckbox
          type={'course'}
          courseTariff={selectedTariff}
          value={order?.value}
          onChange={(value) =>
            onChange('value', value === undefined ? null : value)
          }
        />
      )}
      <div className={classes.footerContainer} style={{ paddingLeft: '8px' }}>
        {selectedTariff && order.value && (
          <CoursePriceView
            tariff={selectedTariff}
            value={order.value}
            withDemo={false}
            promoCode={promoCode}
          />
        )}
        <div className={classes.actions}>
          <SecondaryButton className={classes.cancelButton} onClick={onDismiss}>
            Отменить
          </SecondaryButton>
        </div>
      </div>
    </div>
  )
}

export default SelectCourseCard
